import { FC, useCallback } from 'react';
import Map, { Source, Layer } from 'react-map-gl';
import arrowIcon1 from '../assets/arrow-up-type-1.png';
import arrowIcon2 from '../assets/arrow-up-type-2.png';
import arrowIcon3 from '../assets/arrow-up-type-3.png';
import { colors } from '../utils/colorDict';

const arrowIcons = [arrowIcon1, arrowIcon2, arrowIcon3];

interface SpotlightOverlayProps {
  viewState: any;
  onMove: (evt: any) => void;
  cursorScreenPos: { x: number; y: number } | null;
  mapStyle: string;
  mapboxToken: string;
  filteredItems: any[];
  spotlightRadius?: number;
  spotlightFeather?: number;
}

const SpotlightOverlay: FC<SpotlightOverlayProps> = ({
  viewState,
  onMove,
  cursorScreenPos,
  mapStyle,
  mapboxToken,
  filteredItems,
  spotlightRadius = 150,
  spotlightFeather = 40,
}) => {

  const getMaskStyle = (): React.CSSProperties => {
    if (!cursorScreenPos) {
      return {};
    }
    const { x, y } = cursorScreenPos;
    const mask = `radial-gradient(circle ${spotlightRadius + spotlightFeather}px at ${x}px ${y}px, transparent ${spotlightRadius}px, black ${spotlightRadius + spotlightFeather}px)`;
    return {
      WebkitMaskImage: mask,
      maskImage: mask,
    };
  };

  const featureCollection = {
    type: 'FeatureCollection' as const,
    features: filteredItems || []
  };

  // Load recolored arrow icons onto the overlay map (same logic as pointLayer.jsx)
  const onMapLoad = useCallback((e: any) => {
    const map = e.target;
    arrowIcons.forEach((iconSrc, index) => {
      const image = new Image(24, 24);
      image.src = iconSrc;
      const iconName = `arrowIcon${index + 1}`;
      image.onload = () => {
        if (!map.hasImage(iconName)) {
          const canvas = document.createElement('canvas');
          canvas.width = 24; canvas.height = 24;
          const ctx = canvas.getContext('2d');
          ctx!.drawImage(image, 0, 0, 24, 24);
          const imageData = ctx!.getImageData(0, 0, 24, 24);
          const d = imageData.data;
          const tc = parseInt(colors[1].slice(1), 16);
          const tr = (tc >> 16) & 255, tg = (tc >> 8) & 255, tb = tc & 255;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i+3] === 0) continue;
            const brightness = (d[i] + d[i+1] + d[i+2]) / 3;
            if (brightness < 200) {
              const ratio = brightness / 128;
              d[i]   = Math.min(255, Math.round(tr * ratio));
              d[i+1] = Math.min(255, Math.round(tg * ratio));
              d[i+2] = Math.min(255, Math.round(tb * ratio));
            }
          }
          map.addImage(iconName, { width: 24, height: 24, data: new Uint8Array(d.buffer) });
        }
      };
    });
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        ...getMaskStyle(),
      }}
    >
      <Map
        {...viewState}
        onMove={onMove}
        onLoad={onMapLoad}
        interactive={false}
        mapStyle={mapStyle}
        mapboxAccessToken={mapboxToken}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
      >
        <Source id="overlay-points" type="geojson" data={featureCollection}>
          <Layer
            id="overlay-pointLayer"
            type="symbol"
            layout={{
              "icon-image": [
                'match',
                ['get', 'mediatype'],
                'image', 'arrowIcon1',
                'video', 'arrowIcon2',
                'audio', 'arrowIcon3',
                'arrowIcon1'
              ],
              "icon-rotate": ["get", "angle"],
              // Match the main pointLayer: keep arrows aligned to real-world compass.
              "icon-rotation-alignment": "map",
              'icon-anchor': 'center',
              'icon-allow-overlap': true,
              "icon-size": [
                "interpolate",
                ["linear"],
                ["zoom"],
                10, 0.4,
                14, 0.8,
                18, 1.2
              ]
            }}
          />
        </Source>
      </Map>
    </div>
  );
};

export default SpotlightOverlay;
